import { startCase } from 'lodash';
import classNames from 'classnames';
import {
	AdAccount, Creative, CreativeType,
} from 'wb-reddit/graphql/generated';
import { Carousel } from 'rsuite';
import { useMemo, useState } from 'react';
import redditLogo from 'wb-reddit/assets/reddit-logo.png';
import spotifyLogo from 'wb-spotify/assets/spotify-logo.png';
import {
	ArrowDownIcon,
	ArrowUpIcon,
	CommentIcon,
	isReddit,
	ShareIcon,
	MenuDotsIcon,
} from '@aleph/shared-components';
import { useTranslation } from 'react-i18next';

const RedditMockedPreview = (params: Partial<Creative> & { adAccount: AdAccount, insideCell?: boolean }) => {
	const { t } = useTranslation();
	const isCarousel = params?.creativeType === CreativeType.Carousel;
	const isImage = params?.creativeType === CreativeType.Image;
	const isVideo = params?.creativeType === CreativeType.Video;
	const [selectedCarousel, setSelectedCarousel] = useState(0);
	const selectedDisplayUrl = params?.creativeContent?.[selectedCarousel]?.creativeContentDisplayUrl ?? params?.creativeContent?.[selectedCarousel]?.creativeContentDestinationUrl ?? '';
	const selectedCTA = params?.creativeContent?.[selectedCarousel]?.creativeContentCallToAction;
	const selectedCaption = params?.creativeContent?.[selectedCarousel]?.creativeContentCaption;

	const carouselImages = useMemo(() => params?.creativeContent?.map((content, idx) => (
		<img
			key={idx}
			className="object-contain w-full h-full"
			src={content?.creativeContentMediaUrl ?? ''}
		/>
	)), [JSON.stringify(params?.creativeContent)]);

	return (
		<div
			className={classNames('border flex flex-col gap-[10px] border-[#E2E7E9] rounded-[8px] p-[16px] w-[333px] max-h-full')}
		>
			<div className="flex items-center justify-between gap-[16px] leading-none">
				<div className="flex gap-[4px] items-center text-[12px]">
					<img
						className="rounded-full h-[20px] w-[20px]"
						src={isReddit() ? redditLogo : spotifyLogo}
					/>
					<div className="font-semibold text-[#223237]">u/{params?.adAccount?.adAccountName ?? 'unknown'}</div>
					<div className="text-[#4B6066]">{t('sc: promoted', 'Promoted')}</div>
				</div>

				<div className="text-[#4B6066]">
					<MenuDotsIcon
						width={10}
						height={10}
					/>
				</div>
			</div>

			{
				params.creativeHeadline
				&& <div className="text-[14px] text-black font-semibold leading-none whitespace-normal">
					{params.creativeHeadline}
				</div>
			}

			<div className="overflow-hidden flex">
				{
					isCarousel
					&& <Carousel
						autoplay
						onSlideEnd={setSelectedCarousel}
						className="rounded-[16px] border border-[#E2E7E9] w-[297px] !h-auto max-h-[50vh]"
					>
						{carouselImages}
					</Carousel>
				}

				{
					isImage
					&& <img
						className="rounded-[16px] border border-[#E2E7E9] w-[297px] !h-auto object-contain"
						src={params?.creativeContent?.[0]?.creativeContentMediaUrl ?? ''}
					/>
				}

				{
					isVideo
					&& <div className='rounded-[16px] border border-[#E2E7E9] w-[297px] !h-auto'>
						<video
							className="object-contain w-full h-full"
							controls
							poster={params?.creativeThumbnailUrl ?? ''}
						>
							<source
								src={params.creativeContent?.[0]?.creativeContentMediaUrl ?? ''}
								type="video/mp4"
							/>
						</video>
					</div>
				}
			</div>

			{
				(selectedDisplayUrl || selectedCTA || selectedCaption)
					&& <div className="flex items-center justify-between">
						<div>
							{
								selectedCaption
								&& <div className="text-[11px] text-[##222] leading-none">
									{selectedCaption}
								</div>
							}

							<div className="text-[11px] text-[#82959B] leading-none">
								{selectedDisplayUrl?.length ? selectedDisplayUrl : t('sc: display url', 'Display URL')}
							</div>
						</div>

						{
							selectedCTA
							&& <div className="text-[11px] hover:bg-[#E2E7E9] px-[12px] py-[10px] text-[#0F1A1C] font-semibold leading-none rounded-[16px] bg-[#EAEDEF] cursor-pointer h-[32px]">
								{startCase(selectedCTA?.toLowerCase?.())}
							</div>
						}
					</div>
			}

			<div className="border-b border-b-[#E2E7E9] w-full" />

			<div className="text-[12px] flex gap-[36px] text-[#4B6066] items-center leading-none">
				<div className="flex gap-[8px] items-center">
					<ArrowUpIcon
						width={16}
						height={16}
					/>

					{t('sc: vote', 'Vote')}

					<ArrowDownIcon
						width={16}
						height={16}
					/>
				</div>

				{
					params.creativeAllowComments
					&& <div className="flex gap-[8px] items-center">
						<CommentIcon
							width={16}
							height={16}
						/>

						0
					</div>
				}

				<div className="flex gap-[8px] items-center">
					<ShareIcon
						width={16}
						height={16}
					/>

					{t('sc: share', 'Share')}
				</div>
			</div>
		</div>
	);
};

export default RedditMockedPreview;
